/**
 * The rail as a drawer, under 1024 (redesign §38.3).
 *
 * The rail itself, 236 wide, on the left over the scrim, with « Close menu »
 * beside the mark. It is modal: focus goes into it and stays there, Escape and
 * a click on the scrim close it, the focus goes back to « Menu », and choosing
 * an entry closes it too — the page it opens is what the person came for.
 *
 * The header's breadcrumb leaves with the docked rail's room, so its selectors
 * are stacked at the head of the drawer under the mark — the organization,
 * then the workspace — and choosing one closes the drawer as an entry does.
 */
import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import { color, radius, shadow } from '../theme/tokens';
import { Rail, type CrumbPick } from './Shell';
import type { PageId, RailGroup } from './nav';
import { useMotionMount } from '../lib/presence';

export function RailDrawer({
  open,
  onClose,
  groups,
  page,
  section,
  landing,
  onGo,
  selectors = []
}: {
  open: boolean;
  onClose: () => void;
  groups: RailGroup[];
  page: PageId;
  section: string | null;
  landing: PageId;
  onGo: (page: PageId, section?: string | null) => void;
  /** The breadcrumb's selectors, outermost first, each named for what it switches. */
  selectors?: { label: string; picks: CrumbPick[] }[];
}): React.JSX.Element | null {
  const panel = useRef<HTMLDivElement | null>(null);
  // The rail slides in from its own width on the engine's default spring (d 1
  // r .4) over a veil that fades in; on close the whole fades out in 120 ms (§35).
  const whole = useMotionMount<HTMLDivElement>({}, {}, { o: 0 });
  const veil = useMotionMount<HTMLDivElement>({ o: 0 }, { d: 1, r: 0.35 });
  const slide = useMotionMount<HTMLDivElement>({ x: -236 }, { d: 1, r: 0.4 });
  const panelRef = useCallback(
    (el: HTMLDivElement | null) => {
      panel.current = el;
      slide(el);
    },
    [slide]
  );
  // Held in a ref so a new arrow from the parent's render does not re-run the
  // effect — which would move the focus back to « Close menu » each time.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.querySelector<HTMLElement>('button[aria-label="Close menu"]')?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close.current();
        return;
      }
      // Focus stays inside: Tab from the last control comes back to the first.
      if (event.key !== 'Tab' || !panel.current) return;
      const stops = [...panel.current.querySelectorAll<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])')];
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div ref={whole} role="dialog" aria-modal="true" aria-label="Menu" style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div
        ref={veil}
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.32)' }}
      />
      <div
        ref={panelRef}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          display: 'flex',
          boxShadow: shadow.menu
        }}
      >
        <Rail
          groups={groups}
          page={page}
          section={section}
          landing={landing}
          onGo={(next, nextSection) => {
            onClose();
            onGo(next, nextSection);
          }}
          onClose={onClose}
          head={
            selectors.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 10px' }}>
                {selectors.map((selector) => (
                  <select
                    key={selector.label}
                    aria-label={selector.label}
                    value={selector.picks.find((pick) => pick.current)?.key ?? ''}
                    onChange={(event) => {
                      const pick = selector.picks.find((one) => one.key === event.target.value);
                      if (!pick) return;
                      onClose();
                      pick.onPick();
                    }}
                    style={{
                      width: '100%',
                      height: 32,
                      padding: '0 8px',
                      border: `1px solid ${color.lineInput}`,
                      borderRadius: radius.md,
                      background: color.surface,
                      fontFamily: 'inherit',
                      fontSize: 13,
                      color: color.ink
                    }}
                  >
                    {selector.picks.map((pick) => (
                      <option key={pick.key} value={pick.key}>
                        {pick.label}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
